import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { FaArrowRotateLeft, FaArrowRotateRight } from 'react-icons/fa6';
import { MdRefresh } from 'react-icons/md';
import { PiFlipHorizontalFill, PiFlipVerticalFill } from 'react-icons/pi';
import { TbAlignBoxCenterMiddle } from 'react-icons/tb';
import { Button, IconButton, Slider, Typography, Box } from '@mui/material';
import ModalCropEditor from './ModalCropEditor';

// ── Utility ────────────────────────────────────────────────────────────────

/**
 * TASK-FE-05: InApp Browser detection
 * Phát hiện Facebook, Instagram, Zalo, TikTok, Line, WeChat, Android WebView, iOS WKWebView
 */
function detectInAppBrowser(ua) {
  if (!ua) return false;
  return (
    /FBAN|FBAV|FB_IAB|FBSV|Instagram|Line\/|Zalo|MicroMessenger|TikTok/i.test(ua) ||
    /; ?wv\)/i.test(ua) ||
    (/iPhone|iPod|iPad/i.test(ua) && /AppleWebKit/i.test(ua) && !/Safari/i.test(ua))
  );
}

// ── Component ──────────────────────────────────────────────────────────────

/**
 * AvatarCanvas — canvas editor 960×960 với frame overlay cố định
 *
 * Props:
 *   canvasSize       — số nguyên, mặc định 960
 *   frameSrc         — đường dẫn tới frame PNG transparent
 *   downloadFileName — tên file khi tải về, mặc định 'avatar-hunghau.png'
 */
export default function AvatarCanvas({
  canvasSize = 960,
  frameSrc,
  downloadFileName = 'avatar-hunghau.png',
}) {
  const navigate = useNavigate();
  // ── Fix isIOS hoisting: khai báo TRƯỚC khi dùng trong handler ──
  const isIOS =
    typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  // ── State ──
  const [avatar, setAvatar] = useState({ src: null, height: 0, width: 0, x: 0, y: 0 });
  const [rotate, setRotate] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [flip, setFlip] = useState({ horizontal: false, vertical: false });
  const [mouse, setMouse] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [isWebView, setIsWebView] = useState(false);
  const [saveHintImage, setSaveHintImage] = useState(null);

  // ── Refs ──
  const canvasRef = useRef(null);
  const img_avatar = useRef(null);
  const img_frame = useRef(null);
  const fileInputRef = useRef(null);

  // ── Init ──
  useEffect(() => {
    img_avatar.current = new window.Image();
    img_frame.current = new window.Image();
    setIsWebView(detectInAppBrowser(navigator.userAgent));

    // Load frame
    if (frameSrc) {
      img_frame.current.onload = () => {
        // Vẽ frame lên canvas ngay khi load xong (chưa có avatar)
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasSize, canvasSize);
          ctx.drawImage(img_frame.current, 0, 0, canvasSize, canvasSize);
        }
      };
      img_frame.current.src = frameSrc;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Re-render khi state thay đổi ──
  useEffect(() => {
    if (avatar.src) {
      renderImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotate, flip, zoom, avatar]);

  // ── Canvas render ──
  const renderImage = () => {
    if (!avatar.src || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');

    // Background trắng
    ctx.clearRect(0, 0, canvasSize, canvasSize);
    ctx.save();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasSize, canvasSize);
    ctx.restore();

    // Layer 1: ảnh user (với zoom/rotate/flip/drag)
    ctx.save();
    ctx.translate(canvasSize / 2, canvasSize / 2);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.drawImage(
      img_avatar.current,
      avatar.x - canvasSize / 2,
      avatar.y - canvasSize / 2,
      avatar.width * zoom,
      avatar.height * zoom
    );
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // Layer 2: frame (luôn trên cùng — BR-05)
    if (img_frame.current && img_frame.current.src) {
      ctx.drawImage(img_frame.current, 0, 0, canvasSize, canvasSize);
    }
    ctx.restore();
  };

  // ── File read helper ──
  const readURL = (file) => {
    if (!file) return Promise.reject(new Error('Không có file'));
    return new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = (e) => res(e.target.result);
      reader.onerror = (e) => rej(e);
      reader.readAsDataURL(file);
    });
  };

  // ── Upload handler ──
  const handleUpload = async (file) => {
    if (!file) return;
    const url = await readURL(file);

    // isIOS đã được khai báo ở đầu component (hoisted fix)
    if (isIOS) {
      // iOS workaround: ModalCropEditor sử dụng canvas manipulation gây lỗi trên iOS WebKit.
      // Trên iOS, ảnh được apply trực tiếp vào canvas chính mà không qua modal preview.
      // Người dùng vẫn có thể dùng zoom/rotate/flip/drag sau khi upload.
      handleModalApply(url);
    } else {
      setPendingImage(url);
      setIsModalOpen(true);
    }
  };

  // ── Reset handler ──
  const handleReset = () => {
    setZoom(1);
    setRotate(0);
    setFlip({ horizontal: false, vertical: false });
    setAvatar({ src: null, height: 0, width: 0, x: 0, y: 0 });
    setDownloadUrl(null);
    setSaveHintImage(null);

    // Clear canvas và vẽ lại frame (không có ảnh user)
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvasSize, canvasSize);
      if (img_frame.current && img_frame.current.src) {
        ctx.drawImage(img_frame.current, 0, 0, canvasSize, canvasSize);
      }
    }
  };

  // ── Apply ảnh từ modal vào canvas ──
  const handleModalApply = (processedImageUrl) => {
    setZoom(1);
    setRotate(0);
    setFlip({ horizontal: false, vertical: false });

    if (!canvasRef.current) return;

    img_avatar.current = new window.Image();
    img_avatar.current.onload = () => {
      let h = img_avatar.current.naturalHeight;
      let w = img_avatar.current.naturalWidth;

      // Scale ảnh vừa canvas
      if (w / h > 1) {
        // landscape
        h = canvasSize;
        w = (img_avatar.current.naturalWidth * canvasSize) / img_avatar.current.naturalHeight;
      } else {
        // portrait / square
        w = canvasSize;
        h = (img_avatar.current.naturalHeight * canvasSize) / img_avatar.current.naturalWidth;
      }

      // Ensure không nhỏ hơn canvas
      if (w < canvasSize) {
        const ratio = canvasSize / w;
        w *= ratio;
        h *= ratio;
      }
      if (h < canvasSize) {
        const ratio = canvasSize / h;
        w *= ratio;
        h *= ratio;
      }

      const newAvatar = {
        src: processedImageUrl,
        width: w,
        height: h,
        x: canvasSize / 2 - w / 2,
        y: canvasSize / 2 - h / 2,
      };
      setAvatar(newAvatar);
    };
    img_avatar.current.src = processedImageUrl;
  };

  // ── Drag handlers ──
  const handleMouseDown = (ev) => {
    const isTouch = ev.type === 'touchstart';
    const event = isTouch ? ev.touches[0] : ev;
    setMouse({ x: event.clientX, y: event.clientY });
  };

  const handleMouseMove = (ev) => {
    if (!mouse || !avatar.src) return;
    const isTouch = ev.type === 'touchmove';
    const event = isTouch ? ev.touches[0] : ev;

    const deltaX = (event.clientX - mouse.x) / zoom;
    const deltaY = (event.clientY - mouse.y) / zoom;

    // Rotation-aware delta
    const angle = (-rotate * Math.PI) / 180;
    const rotatedX = deltaX * Math.cos(angle) - deltaY * Math.sin(angle);
    const rotatedY = deltaX * Math.sin(angle) + deltaY * Math.cos(angle);

    const x1 = avatar.x + rotatedX * (flip.horizontal ? -1 : 1);
    const y1 = avatar.y + rotatedY * (flip.vertical ? -1 : 1);
    setAvatar((prev) => ({ ...prev, x: x1, y: y1 }));
    setMouse({ x: event.clientX, y: event.clientY });
  };

  const handleMouseUp = () => setMouse(null);

  // ── Center image ──
  const centerImage = () => {
    if (!avatar.src) return;
    const w = avatar.width * zoom;
    const h = avatar.height * zoom;
    setAvatar((prev) => ({
      ...prev,
      x: canvasSize / 2 - w / 2,
      y: canvasSize / 2 - h / 2,
    }));
  };

  // ── Download ──
  const handleDownload = () => {
    if (!canvasRef.current || !avatar.src) return;
    renderImage();
    const url = canvasRef.current.toDataURL('image/png');

    if (isWebView) {
      setSaveHintImage(url);
      return;
    }

    // Tự động tạo thẻ <a> ảo để trigger download ngay lập tức
    const link = document.createElement('a');
    link.download = downloadFileName || 'avatar-hunghau.png';
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Render ──
  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Canvas container ── */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          backgroundColor: '#f1f5f9',
          borderRadius: 3,
          overflow: 'hidden',
          mb: 2,
        }}
      >
        <canvas
          ref={canvasRef}
          width={canvasSize}
          height={canvasSize}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            cursor: avatar.src ? 'move' : 'default',
          }}
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
        />

        {/* Placeholder khi chưa upload */}
        {!avatar.src && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="40" height="40" fill="none" stroke="#94a3b8" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Chưa có ảnh. Nhấn "Tải ảnh lên" để bắt đầu.
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Controls ── */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mb: 2,
        }}
      >
        {/* Upload button */}
        <Button
          variant="contained"
          component="label"
          sx={{ minHeight: 44, borderRadius: 2, px: 2 }}
        >
          <svg
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            style={{ marginRight: 6 }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
          Tải ảnh lên
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                handleUpload(e.target.files[0]);
                e.target.value = '';
              }
            }}
          />
        </Button>

        <Box sx={{ width: '1px', height: 32, bgcolor: '#e2e8f0', mx: 0.5 }} />

        {/* Rotate left */}
        <IconButton
          title="Xoay trái 90°"
          onClick={() => setRotate((prev) => prev - 90)}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <FaArrowRotateLeft />
        </IconButton>

        {/* Rotate right */}
        <IconButton
          title="Xoay phải 90°"
          onClick={() => setRotate((prev) => prev + 90)}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <FaArrowRotateRight />
        </IconButton>

        {/* Flip horizontal */}
        <IconButton
          title="Lật ngang"
          onClick={() => setFlip((prev) => ({ ...prev, horizontal: !prev.horizontal }))}
          sx={{
            minWidth: 44,
            minHeight: 44,
            backgroundColor: flip.horizontal ? '#dbeafe' : 'transparent',
          }}
        >
          <PiFlipHorizontalFill />
        </IconButton>

        {/* Flip vertical */}
        <IconButton
          title="Lật dọc"
          onClick={() => setFlip((prev) => ({ ...prev, vertical: !prev.vertical }))}
          sx={{
            minWidth: 44,
            minHeight: 44,
            backgroundColor: flip.vertical ? '#dbeafe' : 'transparent',
          }}
        >
          <PiFlipVerticalFill />
        </IconButton>

        {/* Center */}
        <IconButton
          title="Căn giữa ảnh"
          onClick={centerImage}
          sx={{ minWidth: 44, minHeight: 44 }}
          disabled={!avatar.src}
        >
          <TbAlignBoxCenterMiddle />
        </IconButton>

        {/* Reset */}
        <IconButton
          title="Đặt lại"
          onClick={handleReset}
          sx={{ minWidth: 44, minHeight: 44 }}
          disabled={!avatar.src}
        >
          <MdRefresh />
        </IconButton>
      </Box>

      {/* ── Zoom slider ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 36 }}>
          ZOOM
        </Typography>
        <Slider
          min={0.5}
          max={3}
          step={0.01}
          value={zoom}
          onChange={(_, val) => setZoom(val)}
          sx={{ color: '#3b82f6' }}
          disabled={!avatar.src}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 40 }}>
          {zoom.toFixed(2)}x
        </Typography>
      </Box>

      {/* ── Download button ── */}
      <Button
        variant="contained"
        fullWidth
        disabled={!avatar.src}
        onClick={handleDownload}
        sx={{
          minHeight: 52,
          borderRadius: 3,
          fontSize: '1rem',
          fontWeight: 700,
          backgroundColor: '#16a34a',
          '&:hover': { backgroundColor: '#15803d' },
        }}
      >
        <svg
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={{ marginRight: 8 }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        TẢI AVATAR VỀ MÁY
      </Button>

      {/* ── Quay lại trang events ── */}
      <Button
        variant="contained"
        fullWidth
        onClick={() => navigate('/user/events')}
        className="animate-pulse"
        sx={{
          mt: 2,
          minHeight: 52,
          borderRadius: 3,
          fontSize: '1rem',
          fontWeight: 700,
          background: 'linear-gradient(to right, #ff8800, #ff5500)',
          color: '#fff',
          boxShadow: 3,
          '&:hover': {
            background: 'linear-gradient(to right, #e67700, #e64a00)',
          },
        }}
      >
        <FaArrowLeft style={{ marginRight: 8 }} />
        ĐI ĐẾN TRANG MINIGAME
      </Button>


      {/* ── ModalCropEditor ── */}
      <ModalCropEditor
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onApply={handleModalApply}
        initialImage={pendingImage}
      />

      {/* ── Save hint modal cho InApp Browser (TASK-FE-05) ── */}
      {saveHintImage && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            p: 2,
          }}
          onClick={() => setSaveHintImage(null)}
        >
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: 3,
              p: 3,
              maxWidth: 380,
              width: '100%',
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
              Trình duyệt trong ứng dụng này không hỗ trợ tải ảnh trực tiếp.
              <br />
              Nhấn giữ vào ảnh bên dưới rồi chọn <strong>"Lưu ảnh vào máy"</strong> để tải về.
            </Typography>
            <Box
              component="img"
              src={saveHintImage}
              alt="avatar preview"
              sx={{ width: '100%', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}
            />
            <Button
              variant="outlined"
              fullWidth
              sx={{ minHeight: 44 }}
              onClick={() => setSaveHintImage(null)}
            >
              Đóng
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
